import React, { ReactNode, useRef } from 'react';
import { mergeProps, useFocusVisible, useHover, useMenuItem } from 'react-aria';
import { TreeState } from 'react-stately';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';

import { MenuItem, MenuItemLink, MenuItemSection, MenuSingleItem } from '../types';
import { cnListBoxItemSeparator, isBorderElement, useItemClasses } from '../../ListBoxBase/utils';
import { TestProps } from '../../../internal/types/testTypes';
import { MenuIcon } from '../MenuIcon/MenuIcon';
import { cnMenuItem, getMenuItemKey } from '../utils';
import { Separator } from '../../ListBoxBase/Separator/Separator';
import { cn } from '../../../lib/css/className/className';

getPlatformStyles({
    desktop: import('./MenuLink@desktop.css'),
});

const cls = cn('MenuLink');

interface MenuLinkProps<ValueType extends string, SectionType extends string> extends TestProps {
    item: MenuItemLink<ValueType>;
    state: TreeState<MenuItem<ValueType, SectionType>>;
    children: ReactNode;
    //Для закрытия меню
    onClose?: () => void;
}

export const MenuLink = <ValueType extends string, SectionType extends string>(
    props: MenuLinkProps<ValueType, SectionType>,
) => {
    const { item, state, onClose, children } = props;
    const { addonRight, onAction, key, target } = item;
    const ref = useRef<HTMLAnchorElement>(null);
    const isTargetBlank = Boolean(target === '_blank');
    const hasAddonRight = Boolean(addonRight);
    const { isFirst, isLast } = isBorderElement<
        ValueType,
        SectionType,
        MenuSingleItem<ValueType>,
        MenuItemSection<ValueType, SectionType>
    >(
        {
            ...item,
            value: key,
        },
        state.collection,
        getMenuItemKey,
    );

    const { menuItemProps, isDisabled, isFocused } = useMenuItem(
        {
            key,
            onAction: () => {
                onAction?.();
            },
            onClose,
        },
        state,
        ref,
    );
    const { isFocusVisible } = useFocusVisible();
    const { hoverProps, isHovered } = useHover({ isDisabled });
    const { className } = useItemClasses({
        isFocused: (isFocusVisible && isFocused) || isHovered,
        isHovered,
        isDisabled,
        hasAddonRight,
        isFirst,
        isLast,
    });

    const withSeparator = isMobile() && !isLast;
    const Tag = isDisabled ? 'span' : 'a';

    return (
        <>
            <Tag
                {...mergeProps(menuItemProps, hoverProps)}
                ref={ref}
                className={cnMenuItem({ 'has-icon-right': isTargetBlank }, [className])}
                data-testid={props['data-testid']}
                {...hoverProps}
            >
                {children}
                {isTargetBlank && <MenuIcon type={'link'} className={cls('icon', { visible: isHovered })} />}
            </Tag>
            {withSeparator && <Separator className={cnListBoxItemSeparator(null)} />}
        </>
    );
};
